<div class="modal-container customer-dialog">
  <div class="modal-content">
    <h1 class="modal-title">{{ isAddMode ? 'Add New Customer' : 'Edit Customer' }}</h1>

    <div *ngIf="isLoadingCustomer" class="loading-overlay">
      <mat-spinner diameter="36"></mat-spinner>
      <span>Loading customer data…</span>
    </div>

    <form *ngIf="customerListForm" [formGroup]="customerListForm" [class.form-loading]="isLoadingCustomer">

      <input *ngIf="isEditMode" type="hidden" id="id" formControlName="id">

      <!-- Edit mode: Customer ID -->
      <div class="form-grid" *ngIf="isEditMode">
        <div class="form-group">
          <label for="customerId">Customer ID</label>
          <input
            type="text"
            id="customerId"
            formControlName="customerId"
            class="form-control disabled"
            readonly
          >
        </div>
      </div>

      <!-- Contact Information -->
      <h2 class="section-title section-title--full">Contact Information</h2>

      <!-- First Name | Last Name | Country -->
      <div class="form-grid">
        <div class="form-group">
          <label for="contactFirstName">First Name</label>
          <input
            type="text"
            id="contactFirstName"
            formControlName="contactFirstName"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('contactFirstName')"
            placeholder="Enter first name"
            autocomplete="given-name"
            (keydown)="onPersonNameKeydown($event)"
            (input)="onPersonNameInput('contactFirstName', $event)"
          />
          <small class="form-error" *ngIf="getFieldErrorMessage('contactFirstName') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="contactLastName">Last Name</label>
          <input
            type="text"
            id="contactLastName"
            formControlName="contactLastName"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('contactLastName')"
            placeholder="Enter last name"
            autocomplete="family-name"
            (keydown)="onPersonNameKeydown($event)"
            (input)="onPersonNameInput('contactLastName', $event)"
          />
          <small class="form-error" *ngIf="getFieldErrorMessage('contactLastName') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="country">Country</label>
          <select
            id="country"
            formControlName="country"
            class="form-select form-control"
            [class.is-invalid]="isFieldInvalid('country')"
          >
            <option value="">Select country</option>
            <option *ngFor="let country of usaCountries" [ngValue]="country.name">
              {{ country.name }}
            </option>
          </select>
          <small class="form-error" *ngIf="getFieldErrorMessage('country') as err">{{ err }}</small>
        </div>
      </div>

      <!-- State | City | Phone Number -->
      <div class="form-grid">
        <div class="form-group">
          <label for="state">State</label>
          <select
            id="state"
            formControlName="state"
            class="form-select form-control"
            [class.is-invalid]="isFieldInvalid('state')"
          >
            <option value="">Select state</option>
            <option *ngFor="let state of usaStates" [ngValue]="state.name">
              {{ state.name }}
            </option>
          </select>
          <small class="form-error" *ngIf="getFieldErrorMessage('state') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="city">City</label>
          <select
            id="city"
            formControlName="city"
            class="form-select form-control"
            [class.is-invalid]="isFieldInvalid('city')"
          >
            <option value="">Select city</option>
            <option *ngFor="let city of availableCities" [ngValue]="city">
              {{ city }}
            </option>
          </select>
          <small class="form-error" *ngIf="getFieldErrorMessage('city') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="phoneNumber">
            Phone Number
          </label>
          <input
            type="text"
            id="phoneNumber"
            formControlName="phoneNumber"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('phoneNumber')"
            [placeholder]="usPhoneNumberPlaceholder"
            inputmode="tel"
            autocomplete="tel"
          />
          <small class="form-error" *ngIf="getFieldErrorMessage('phoneNumber') as err">{{ err }}</small>
        </div>
      </div>

      <!-- Zip Code | Address | Status -->
      <div class="form-grid form-grid--address-row">
        <div class="form-group">
          <label for="zip">Zip Code</label>
          <input
            type="text"
            id="zip"
            formControlName="zip"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('zip')"
            placeholder="Enter zip code"
          />
          <small class="form-error" *ngIf="getFieldErrorMessage('zip') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="address">Address</label>
          <textarea
            id="address"
            formControlName="address"
            class="form-control form-control--textarea"
            [class.is-invalid]="isFieldInvalid('address')"
            rows="2"
            placeholder="Enter address"
            autocomplete="street-address"
          ></textarea>
          <small class="form-error" *ngIf="getFieldErrorMessage('address') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="isActive">Status</label>
          <select
            id="isActive"
            formControlName="isActive"
            class="form-select form-control"
            [class.disabled]="isAddMode"
          >
            <option [ngValue]="true">Active</option>
            <option *ngIf="isEditMode" [ngValue]="false">Inactive</option>
          </select>
        </div>
      </div>

      <!-- Organization Information -->
      <h2 class="section-title section-title--full">Organization Information</h2>

      <!-- Organization Name | Account Number | Organization Contact Email -->
      <div class="form-grid">
        <div class="form-group">
          <label for="customerName">
            Organization Name<span class="required">*</span>
          </label>
          <input
            type="text"
            id="customerName"
            formControlName="customerName"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('customerName')"
            placeholder="Enter organization name"
          >
          <small class="form-error" *ngIf="getFieldErrorMessage('customerName') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="accountNumber">
            Account Number<span class="required">*</span>
          </label>
          <input
            type="text"
            id="accountNumber"
            formControlName="accountNumber"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('accountNumber')"
            placeholder="Enter account number"
          >
          <small class="form-error" *ngIf="getFieldErrorMessage('accountNumber') as err">{{ err }}</small>
          <small
            class="form-info"
            *ngIf="!getFieldErrorMessage('accountNumber') && accountNumberInfoMessage"
          >{{ accountNumberInfoMessage }}</small>
        </div>
        <div class="form-group">
          <label for="contactEmail">
            Organization Contact Email<span class="required">*</span>
          </label>
          <input
            type="text"
            id="contactEmail"
            formControlName="contactEmail"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('contactEmail')"
            placeholder="Enter organization contact email"
            autocomplete="email"
            inputmode="email"
          />
          <small class="form-error" *ngIf="getFieldErrorMessage('contactEmail') as err">{{ err }}</small>
        </div>
      </div>

      <!-- Organization Type | Cloud Provider | Parent Organization -->
      <div class="form-grid">
        <div class="form-group">
          <label for="customerTypeId">
            Organization Type<span class="required">*</span>
          </label>
          <select
            id="customerTypeId"
            formControlName="customerTypeId"
            class="form-select form-control"
            [class.disabled]="isResellerCustomerLocked"
            [class.is-invalid]="isFieldInvalid('customerTypeId')"
          >
            <option [ngValue]="null" disabled>Select organization type</option>
            <option *ngFor="let type of visibleCustomerTypes" [ngValue]="type.typeId">
              {{ type.label }}
            </option>
          </select>
          <small class="form-error" *ngIf="getFieldErrorMessage('customerTypeId') as err">{{ err }}</small>
        </div>
        <div class="form-group">
          <label for="cloudProvider">Cloud Provider</label>
          <select
            id="cloudProvider"
            formControlName="cloudProvider"
            class="form-select form-control"
          >
            <option value="">Please Choose</option>
            <option value="AWS">AWS</option>
            <option value="GCP">GCP</option>
            <option value="WMT-GCP">WMT-GCP</option>
          </select>
        </div>
        <div class="form-group" *ngIf="showParentCustomer; else parentOrgSpacer">
          <label for="parentResellerId">
            Parent Organization<span class="required">*</span>
          </label>
          <select
            *ngIf="!isParentOrganizationLocked; else lockedParentOrganization"
            id="parentResellerId"
            formControlName="parentResellerId"
            class="form-select form-control"
            [class.is-invalid]="isFieldInvalid('parentResellerId')"
          >
            <option [ngValue]="null" disabled>Select parent organization</option>
            <option
              *ngFor="let reseller of resellers"
              [ngValue]="resolveResellerId(reseller)"
            >
              {{ reseller.customerName }}
            </option>
          </select>
          <ng-container *ngIf="!isParentOrganizationLocked">
            <small class="form-error" *ngIf="getFieldErrorMessage('parentResellerId') as err">{{ err }}</small>
          </ng-container>
          <ng-template #lockedParentOrganization>
            <select
              id="parentResellerIdLocked"
              class="form-select form-control disabled"
              disabled
            >
              <option>{{ lockedParentOrganizationDisplayLabel }}</option>
            </select>
          </ng-template>
        </div>
        <ng-template #parentOrgSpacer>
          <div class="form-group form-group--spacer" aria-hidden="true"></div>
        </ng-template>
      </div>

      <!-- Wiliot Integration Toggle (hidden; logic preserved) -->
      <div class="wiliot-integration-group">
        <label class="wiliot-section-label">Wiliot Integration</label>
        <div class="wiliot-toggle-card" (click)="toggleWiliot()">
          <div class="wiliot-toggle-left">
            <div class="toggle-switch" [class.toggle-switch--on]="wiliotEnabled">
              <span class="toggle-thumb"></span>
            </div>
            <div class="wiliot-toggle-text">
              <span class="wiliot-toggle-title">Enable Wiliot Integration</span>
              <span class="wiliot-toggle-desc">
                When enabled, this customer has access to Wiliot IoT features and tracking capabilities.
              </span>
            </div>
          </div>
          <span class="wiliot-badge" [class.wiliot-badge--on]="wiliotEnabled">
            {{ wiliotEnabled ? 'ON' : 'OFF' }}
          </span>
        </div>
      </div>

      <div class="form-grid form-grid--wiliot-format">
        <div class="form-group">
          <label for="wiliotGatewayNameFormat" class="wiliot-gateway-format-label">
            Wiliot Gateway Naming Format
            <span
              class="wiliot-gateway-format-info-wrap"
              (mouseenter)="openWiliotGatewayTokenPopover()"
              (mouseleave)="closeWiliotGatewayTokenPopover()"
            >
              <button
                type="button"
                class="wiliot-gateway-format-info"
                aria-describedby="wiliotGatewayTokenPopover"
                aria-label="Supported tokens for Wiliot Gateway Naming Format"
                tabindex="0"
                (focus)="openWiliotGatewayTokenPopover()"
                (blur)="closeWiliotGatewayTokenPopover()"
              >
                <span class="wiliot-gateway-format-info__glyph" aria-hidden="true">i</span>
              </button>

              <div
                *ngIf="showWiliotGatewayTokenPopover"
                id="wiliotGatewayTokenPopover"
                class="wiliot-gateway-token-popover"
                role="tooltip"
                aria-label="Supported tokens"
              >
                <div class="wiliot-gateway-token-popover__header">
                  <h3 class="wiliot-gateway-token-popover__title">Supported tokens</h3>
                </div>
                <ul class="wiliot-gateway-token-popover__list">
                  <li *ngFor="let item of wiliotGatewayTokenHelp" class="wiliot-gateway-token-popover__row">
                    <code class="wiliot-gateway-token-popover__token">{{ item.token }}</code>
                    <span class="wiliot-gateway-token-popover__desc">{{ item.description }}</span>
                  </li>
                </ul>
              </div>
            </span>
          </label>
          <input
            type="text"
            id="wiliotGatewayNameFormat"
            formControlName="wiliotGatewayNameFormat"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('wiliotGatewayNameFormat')"
            placeholder="e.g. %AssetID%-MG14"
            maxlength="255"
            autocomplete="off"
          />
          <small class="form-error" *ngIf="getFieldErrorMessage('wiliotGatewayNameFormat') as err">{{ err }}</small>
        </div>
      </div>

      <div class="button-container">
        <button mat-stroked-button mat-dialog-close type="button" class="btn-cancel"
                [disabled]="isSubmitting">
          Cancel
        </button>
        <button type="button" class="btn-confirm"
                (click)="onSubmit()"
                [disabled]="isSubmitDisabled"
                [attr.aria-disabled]="isSubmitDisabled">
          {{ isSubmitting ? 'Saving...' : (isAddMode ? 'Add Customer' : 'Update Customer') }}
        </button>
      </div>
    </form>

  </div>
</div>
